import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  type ValidatorFn,
} from "@angular/forms";
import { DateTime } from "luxon";
import { EventDate } from "../../../../../../../shared/components/cards/card-manage-event/types/event-date.type";
import { TicketBatch } from "../../../../../../../shared/components/modal-tickets/types/ticket-batch.type";
import { ticketsDateRangeValidatorDateTime } from "./validators/tickets-date-range.validator";
import {
  BatchTransitionMode,
  hasBatchPredecessor,
} from "../../../../../../../shared/helpers/batch-transition/batch-transition.helper";

type BuildBatchEditModalFormParams = {
  formBuilder: FormBuilder;
  eventDate: EventDate;
  isEditMode: boolean;
  ticketBatches: TicketBatch[];
  selectedBatch?: TicketBatch; // só em edição
};

export type BatchEditModalForm = FormGroup<{
  transitionMode: FormControl<BatchTransitionMode>;
  absorbsPlatformFee: FormControl<boolean>;
  absorbsProcessingFee: FormControl<boolean>;
  soldAtPdv: FormControl<boolean>;
  hasHalfPrice: FormControl<boolean>;
  ticketsDateGroup: FormGroup<{
    startDateTime: FormControl<Date | null>;
    endDateTime: FormControl<Date | null>;
  }>;
}>;

export function buildBatchEditModalForm(
  params: BuildBatchEditModalFormParams,
): BatchEditModalForm {
  const { formBuilder, eventDate, isEditMode, ticketBatches, selectedBatch } =
    params;

  const eventEndDateTime = buildEventEndDateTimeOrNull(eventDate);

  const defaultStartDateTime = isEditMode
    ? toJsDateOrNull(selectedBatch?.sale_start)
    : buildDefaultStartForCreate(ticketBatches, eventEndDateTime);

  const eventStart = DateTime.fromISO(
    `${eventDate.startDate}T${eventDate.startTime}`,
  );
  const closingLimit =
    eventStart.isValid &&
    defaultStartDateTime &&
    defaultStartDateTime.getTime() < eventStart.toMillis()
      ? eventStart
      : eventEndDateTime;

  const defaultEndDateTime = isEditMode
    ? toJsDateOrNull(selectedBatch?.sale_end)
    : closingLimit
      ? closingLimit.toJSDate()
      : null;

  const dateValidator = ticketsDateRangeValidatorDateTime(eventDate, {
    validateNotBeforeNow: !isEditMode,
    ticketBatches,
    selectedBatch,
  });
  const groupValidators: ValidatorFn[] = [
    (group) => {
      if (group.parent?.get("transitionMode")?.value !== "sold_out_only")
        return dateValidator(group);
      if (hasBatchPredecessor(ticketBatches, selectedBatch)) return null;
      const start = group.get("startDateTime")?.value as Date | null;
      if (!start || !eventEndDateTime) return null;
      if (!isEditMode && start.getTime() < Date.now())
        return { dateBeforeCurrent: true };
      return start.getTime() >= eventEndDateTime.toMillis()
        ? { startDateAfterEventEnd: true }
        : null;
    },
  ];

  const ticketsDateGroup = formBuilder.group(
    {
      startDateTime: formBuilder.control<Date | null>(defaultStartDateTime, {
        validators: [Validators.required],
      }),
      endDateTime: formBuilder.control<Date | null>(defaultEndDateTime, {
        validators: [Validators.required],
      }),
    },
    { validators: groupValidators },
  );

  return formBuilder.group({
    transitionMode: formBuilder.control<BatchTransitionMode>(
      selectedBatch?.transition_mode ?? "date_or_sold_out",
      { nonNullable: true },
    ),
    absorbsPlatformFee: formBuilder.control<boolean>(
      selectedBatch?.absorbs_platform_fee ?? false,
      { nonNullable: true },
    ),
    absorbsProcessingFee: formBuilder.control<boolean>(
      selectedBatch?.absorbs_processing_fee ?? false,
      { nonNullable: true },
    ),
    soldAtPdv: formBuilder.control<boolean>(
      selectedBatch?.sold_at_pdv ?? false,
      { nonNullable: true },
    ),
    hasHalfPrice: formBuilder.control<boolean>(
      selectedBatch?.has_half_price ?? false,
      { nonNullable: true },
    ),
    ticketsDateGroup,
  }) as BatchEditModalForm;
}

function buildDefaultStartForCreate(
  ticketBatches: TicketBatch[],
  eventEnd: DateTime | null,
): Date | null {
  const lastBatchEndDateTime = getLastBatchEndDateTimeOrNull(ticketBatches);
  const nextMinute = DateTime.fromMillis(Math.ceil(Date.now() / 60000) * 60000);
  const start = lastBatchEndDateTime && lastBatchEndDateTime > nextMinute
    ? lastBatchEndDateTime : nextMinute;
  // Adjacent lots share a boundary; do not invent a one-minute sales gap or a
  // default opening after the event has ended.
  return eventEnd && start >= eventEnd ? null : start.toJSDate();
}

function getLastBatchEndDateTimeOrNull(
  ticketBatches: TicketBatch[],
): DateTime | null {
  const validEndDates: DateTime[] = ticketBatches
    .map((ticketBatch) => DateTime.fromISO(ticketBatch?.sale_end ?? ""))
    .filter((dateTime) => dateTime.isValid);

  if (validEndDates.length === 0) return null;

  return validEndDates.reduce((maxDateTime, currentDateTime) =>
    currentDateTime > maxDateTime ? currentDateTime : maxDateTime,
  );
}

function toJsDateOrNull(isoDateTime?: string | null): Date | null {
  if (!isoDateTime) return null;

  const parsedDateTime = DateTime.fromISO(isoDateTime);
  return parsedDateTime.isValid ? parsedDateTime.toJSDate() : null;
}

function buildEventEndDateTimeOrNull(eventDate: EventDate): DateTime | null {
  if (!eventDate?.endDate || !eventDate?.endTime) return null;

  const isoCandidate = `${eventDate.endDate}T${eventDate.endTime}`;
  const parsedDateTime = DateTime.fromISO(isoCandidate);

  return parsedDateTime.isValid ? parsedDateTime : null;
}
